import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';

import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@tyro/core/dialog';
import { RHFTextField } from '@tyro/core/form';
import { useFormValidator } from '@tyro/core/hooks';
import { useTranslation } from '@tyro/i18n';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useSaveTimelineHighlight } from '../../../../api/student/timeline';
import {
  RHFStaffAutocomplete,
  type StaffSelectOption,
} from '../../staff-autocomplete';
import type { TimelineItemProps } from './item';
import { EventsDetails } from './previewer/highlight-card';

type FormState = {
  title: string;
  description: string;
  assignedTo: StaffSelectOption[];
};

export type AddHighlightModalProps = {
  open: boolean;
  events: TimelineItemProps['event'][];
  onClose: () => void;
  onSuccess: () => void;
};

export function AddHighlightModal({
  open,
  events,
  onSuccess,
  onClose,
}: AddHighlightModalProps) {
  const { t } = useTranslation(['common', 'people']);

  const { resolver, rules } = useFormValidator<FormState>();

  const { control, handleSubmit, reset } = useForm<FormState>({
    resolver: resolver({
      title: rules.required(),
      description: rules.required(),
      assignedTo: rules.required(),
    }),
  });

  const { mutate: createHighlight, isPending: isCreatingHighlight } =
    useSaveTimelineHighlight();

  const onSubmit = handleSubmit(({ title, description, assignedTo }) => {
    createHighlight(
      {
        title,
        highlight: description,
        assignedToPartyIds: assignedTo.map(({ partyId }) => partyId),
        studentPartyId: events?.[0]?.id?.studentPartyId,
        events: events.map((event) => ({
          eventId: event.id.entityId,
          eventType: event.id.entityType,
        })),
      },
      {
        onSuccess: () => {
          onClose();
          onSuccess();
        },
      },
    );
  });

  useEffect(() => {
    reset();
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      scroll="paper"
      fullWidth
      maxWidth="sm"
    >
      <form onSubmit={onSubmit}>
        <DialogTitle onClose={onClose}>
          {t('people:highlightDetails')}
        </DialogTitle>
        <DialogContent>
          <Stack
            gap={2}
            mt={1}
            sx={{
              '& .timeline-item-icon': {
                width: 26,
                height: 26,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                borderRadius: '50%',

                '& svg': {
                  width: 18,
                  height: 18,
                },
              },
            }}
          >
            <RHFTextField
              label={t('common:title')}
              controlProps={{
                name: 'title',
                control,
              }}
            />
            <RHFTextField
              label={t('common:description')}
              controlProps={{
                name: 'description',
                control,
              }}
              textFieldProps={{
                fullWidth: true,
                multiline: true,
                rows: 4,
              }}
            />
            <RHFStaffAutocomplete
              label={t('people:assignedTo')}
              multiple
              controlProps={{
                name: 'assignedTo',
                control,
              }}
            />
            <EventsDetails events={events} />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button variant="soft" color="inherit" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>

          <Button
            type="submit"
            variant="contained"
            loading={isCreatingHighlight}
          >
            {t('common:actions.save')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
